import React, { useCallback, useEffect, useState } from 'react';
import {
  Code2, Copy, Edit3, Eye, EyeOff, Handshake, Image as ImgIcon, Loader2,
  MousePointerClick, Plus, Trash2, Upload, X,
} from 'lucide-react';
import client, { resolveApiAssetUrl } from '../lib/api';
import { UPLOAD_MAX_LABEL, uploadTooLarge } from '../lib/mediaAsset';
import { useToast } from '../hooks/use-toast';

/**
 * Sponsorluk yönetimi.
 *
 * İki iş yapar: footer'da görünen sponsorluk kayıtlarını düzenlemek ve her
 * kayıt için **üçüncü taraf sitelere verilecek banner kodunu** üretmek.
 * Kod istemcide birleştirilmez — adres tabanı sunucudan gelir
 * (`GET /admin/sponsorships/{id}/embed`), çünkü panel ayrı bir alan adında
 * çalışıyor olabilir ve `window.location`'dan türetilen bir taban panelin
 * adresini dışarıya dağıtırdı.
 */
const emptyForm = {
  slug: '',
  name: '',
  partner_type: 'club',
  tier: 'official',
  season: '',
  logo_url: '',
  banner_image_url: '',
  description: '',
  link: '',
  accent_color: '#0ea5e9',
  show_in_footer: true,
  embed_enabled: true,
  embed_target: '',
  order: 0,
  is_active: true,
};

const Sponsorships = () => {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ sizes: [], tiers: [], types: [], site_base: '' });
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState('');
  // Banner penceresi: hangi kayıt, hangi boyut ve sunucudan gelen kod.
  const [embed, setEmbed] = useState(null);
  const [embedSize, setEmbedSize] = useState('leaderboard');
  const [embedCode, setEmbedCode] = useState(null);
  const [embedLoading, setEmbedLoading] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await client.get('/admin/sponsorships');
      setItems(data.items || []);
      setMeta({
        sizes: data.sizes || [],
        tiers: data.tiers || [],
        types: data.types || [],
        site_base: data.site_base || '',
      });
    } catch (error) {
      toast({
        title: 'Sponsorluklar yüklenemedi',
        description: error.response?.data?.detail || 'İstek başarısız.',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(emptyForm); setModal({ mode: 'new' }); };
  const openEdit = (item) => {
    setForm({ ...emptyForm, ...item });
    setModal({ mode: 'edit', id: item.id });
  };

  const uploadImage = async (event, field) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (uploadTooLarge(file)) {
      toast({ title: 'Görsel yüklenemedi', description: `Dosya en fazla ${UPLOAD_MAX_LABEL} olabilir.` });
      return;
    }
    setUploading(field);
    try {
      const body = new FormData();
      body.append('file', file);
      const { data } = await client.post('/admin/uploads/images', body);
      setForm((current) => ({ ...current, [field]: data.url }));
      toast({ title: 'Görsel yüklendi' });
    } catch (error) {
      toast({
        title: 'Görsel yüklenemedi',
        description: error.response?.data?.detail || 'Dosya yükleme başarısız.',
      });
    } finally {
      setUploading('');
    }
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast({ title: 'Ad zorunlu', description: 'Sponsorluğun adını yazın.' });
      return;
    }
    setBusy(true);
    try {
      if (modal.mode === 'new') await client.post('/admin/sponsorships', form);
      else await client.patch(`/admin/sponsorships/${modal.id}`, form);
      toast({ title: 'Kaydedildi' });
      setModal(null);
      load();
    } catch (error) {
      toast({
        title: 'Kaydedilemedi',
        description: error.response?.data?.detail || 'İstek başarısız.',
      });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(
      `"${item.name}" sponsorluğu silinsin mi? Bu kayda ait dağıtılmış bannerlar da çalışmayı bırakır.`,
    )) return;
    try {
      await client.delete(`/admin/sponsorships/${item.id}`);
      toast({ title: 'Silindi' });
      load();
    } catch (error) {
      toast({ title: 'Silinemedi', description: error.response?.data?.detail || 'İstek başarısız.' });
    }
  };

  const toggle = async (item) => {
    try {
      await client.patch(`/admin/sponsorships/${item.id}`, { is_active: !item.is_active });
      load();
    } catch (error) {
      toast({ title: 'Güncellenemedi', description: error.response?.data?.detail || 'İstek başarısız.' });
    }
  };

  const loadEmbed = useCallback(async (item, size) => {
    setEmbedLoading(true);
    try {
      const { data } = await client.get(`/admin/sponsorships/${item.id}/embed`, {
        params: { size },
      });
      setEmbedCode(data);
    } catch (error) {
      setEmbedCode(null);
      toast({
        title: 'Banner kodu alınamadı',
        description: error.response?.data?.detail || 'İstek başarısız.',
      });
    } finally {
      setEmbedLoading(false);
    }
  }, [toast]);

  const openEmbed = (item) => {
    const size = meta.sizes[0]?.key || 'leaderboard';
    setEmbed(item);
    setEmbedSize(size);
    setEmbedCode(null);
    loadEmbed(item, size);
  };

  const pickSize = (size) => {
    setEmbedSize(size);
    if (embed) loadEmbed(embed, size);
  };

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast({ title: `${label} kopyalandı` });
    } catch {
      // Panel HTTPS dışında açıldığında pano API'si yoktur; kullanıcı kod
      // kutusundan elle seçebilsin diye sessizce geçilmez, söylenir.
      toast({
        title: 'Kopyalanamadı',
        description: 'Kod kutusundaki metni elle seçip kopyalayın.',
      });
    }
  };

  const tierLabel = (key) => meta.tiers.find((t) => t.key === key)?.label || key;
  const typeLabel = (key) => meta.types.find((t) => t.key === key)?.label || key;

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ background: 'rgba(14,165,233,0.15)', color: 'var(--v2-info, #0ea5e9)' }}
        >
          <Handshake size={18} />
        </div>
        <div>
          <h1 className="text-xl font-bold">Sponsorluklar</h1>
          <p className="text-xs text-slate-400">
            Footer sponsorluk bandı ve diğer sitelere verilen banner kodları
          </p>
        </div>
        <button onClick={openNew} className="admin-btn-primary ml-auto flex items-center gap-2">
          <Plus size={14} /> Yeni Sponsorluk
        </button>
      </div>

      {loading ? (
        <div className="admin-card p-10 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>
      ) : items.length === 0 ? (
        <div className="admin-card p-10 text-center text-slate-500">
          Henüz sponsorluk eklenmemiş. Kulüp, lig ya da organizasyon ekleyin;
          footer bandı ve banner kodları otomatik oluşur.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {items.map((item) => (
            <div key={item.id} className="admin-card p-4">
              <div className="flex items-start gap-3">
                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: `${item.accent_color || '#0ea5e9'}1f`,
                    border: `1px solid ${item.accent_color || '#0ea5e9'}44`,
                  }}
                >
                  {item.logo_url ? (
                    <img
                      src={resolveApiAssetUrl(item.logo_url)}
                      alt=""
                      className="max-w-[52px] max-h-[52px] object-contain"
                    />
                  ) : (
                    <ImgIcon size={22} className="text-slate-500" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className="text-[10px] font-black tracking-[0.14em]"
                    style={{ color: item.accent_color || '#0ea5e9' }}
                  >
                    {tierLabel(item.tier)}
                  </div>
                  <div className="font-bold truncate">{item.name}</div>
                  <div className="text-xs text-slate-400">
                    {[typeLabel(item.partner_type), item.season].filter(Boolean).join(' · ')}
                  </div>
                  <div className="mt-1 flex flex-wrap gap-1">
                    <span className={`badge-pill ${item.is_active ? 'badge-green' : 'badge-gray'}`}>
                      {item.is_active ? 'Aktif' : 'Pasif'}
                    </span>
                    {item.show_in_footer && <span className="badge-pill badge-blue">Footer</span>}
                    <span className={`badge-pill ${item.embed_enabled ? 'badge-blue' : 'badge-gray'}`}>
                      {item.embed_enabled ? 'Banner açık' : 'Banner kapalı'}
                    </span>
                    <span className="badge-pill badge-gray">/{item.slug}</span>
                  </div>
                </div>
              </div>

              <div className="mt-3 flex items-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1">
                  <Eye size={12} /> {(item.impressions || 0).toLocaleString('tr-TR')} gösterim
                </span>
                <span className="flex items-center gap-1">
                  <MousePointerClick size={12} /> {(item.clicks || 0).toLocaleString('tr-TR')} tıklama
                </span>
              </div>

              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => openEmbed(item)}
                  className="admin-btn-primary flex-1 py-2 text-xs flex items-center justify-center gap-1"
                >
                  <Code2 size={12} /> Banner Kodu
                </button>
                <button onClick={() => openEdit(item)} className="admin-btn-secondary py-2 px-3 text-xs flex items-center gap-1">
                  <Edit3 size={12} /> Düzenle
                </button>
                <button onClick={() => toggle(item)} className="icon-btn view">
                  {item.is_active ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
                <button onClick={() => remove(item)} className="icon-btn ban"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {modal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <h3 className="font-bold">
                {modal.mode === 'new' ? 'Yeni Sponsorluk' : 'Sponsorluğu Düzenle'}
              </h3>
              <button onClick={() => setModal(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5 grid grid-cols-2 gap-3">
              <F l="Ad (ör: Deportivo Alavés)" full>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="admin-input"
                  placeholder="Kulüp / lig / organizasyon adı"
                />
              </F>
              <F l="Sponsorluk Türü">
                <select
                  value={form.partner_type}
                  onChange={(e) => setForm({ ...form, partner_type: e.target.value })}
                  className="admin-input"
                >
                  {meta.types.map((type) => (
                    <option key={type.key} value={type.key}>{type.label}</option>
                  ))}
                </select>
              </F>
              <F l="Derece (rozet yazısı)">
                <select
                  value={form.tier}
                  onChange={(e) => setForm({ ...form, tier: e.target.value })}
                  className="admin-input"
                >
                  {meta.tiers.map((tier) => (
                    <option key={tier.key} value={tier.key}>{tier.label}</option>
                  ))}
                </select>
              </F>
              <F l="Sezon (ör: 2026/27)">
                <input
                  value={form.season}
                  onChange={(e) => setForm({ ...form, season: e.target.value })}
                  className="admin-input"
                />
              </F>
              <F l="Vurgu Rengi (kulüp rengi)">
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={/^#[0-9a-fA-F]{6}$/.test(form.accent_color) ? form.accent_color : '#0ea5e9'}
                    onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    className="h-[38px] w-[52px] shrink-0 rounded-lg border border-white/10 bg-transparent"
                  />
                  <input
                    value={form.accent_color}
                    onChange={(e) => setForm({ ...form, accent_color: e.target.value })}
                    className="admin-input min-w-0 flex-1"
                    placeholder="#0b3d91"
                  />
                </div>
              </F>
              <F l="Sponsor Logosu" full>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    value={form.logo_url}
                    onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                    className="admin-input min-w-0 flex-1"
                    placeholder="Görsel URL'si girin"
                  />
                  <label className={`admin-btn-secondary flex shrink-0 cursor-pointer items-center justify-center gap-2 ${uploading === 'logo_url' ? 'pointer-events-none opacity-60' : ''}`}>
                    {uploading === 'logo_url' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    Yükle
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                      className="sr-only"
                      disabled={uploading === 'logo_url'}
                      onChange={(event) => uploadImage(event, 'logo_url')}
                    />
                  </label>
                </div>
                <p className="mt-1 text-[11px] text-slate-500">
                  Şeffaf zeminli PNG/SVG önerilir; footer bandında ve banner'da bu görsel kullanılır.
                </p>
              </F>
              <F l="Banner Zemin Görseli (isteğe bağlı)" full>
                <div className="flex flex-col gap-2 sm:flex-row">
                  <input
                    value={form.banner_image_url}
                    onChange={(e) => setForm({ ...form, banner_image_url: e.target.value })}
                    className="admin-input min-w-0 flex-1"
                    placeholder="Boş bırakılırsa banner zemini vurgu renginden üretilir"
                  />
                  <label className={`admin-btn-secondary flex shrink-0 cursor-pointer items-center justify-center gap-2 ${uploading === 'banner_image_url' ? 'pointer-events-none opacity-60' : ''}`}>
                    {uploading === 'banner_image_url' ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                    Yükle
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml,.svg"
                      className="sr-only"
                      disabled={uploading === 'banner_image_url'}
                      onChange={(event) => uploadImage(event, 'banner_image_url')}
                    />
                  </label>
                </div>
              </F>
              <F l="Açıklama (sponsorluk sayfasında görünür)" full>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="admin-input"
                  style={{ minHeight: 70, padding: 12 }}
                />
              </F>
              <F l="Sponsorun Sitesi">
                <input
                  value={form.link}
                  onChange={(e) => setForm({ ...form, link: e.target.value })}
                  className="admin-input"
                  placeholder="https://deportivoalaves.com/"
                />
              </F>
              <F l="Banner Tıklama Hedefi">
                <input
                  value={form.embed_target}
                  onChange={(e) => setForm({ ...form, embed_target: e.target.value })}
                  className="admin-input"
                  placeholder="Boşsa site ana sayfası"
                />
              </F>
              <F l="Kısa Ad (banner adresi)">
                <input
                  value={form.slug}
                  onChange={(e) => setForm({ ...form, slug: e.target.value })}
                  className="admin-input"
                  placeholder="Boş bırakılırsa addan üretilir"
                />
                <p className="mt-1 text-[11px] text-amber-300/80">
                  Değiştirilirse dağıtılmış bannerlar kırılır.
                </p>
              </F>
              <F l="Sıra">
                <input
                  type="number"
                  value={form.order}
                  onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  className="admin-input"
                />
              </F>
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="accent-cyan-500"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                />
                Yayında (kapatılırsa footer bandı ve bannerlar susar)
              </label>
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="accent-cyan-500"
                  checked={form.show_in_footer}
                  onChange={(e) => setForm({ ...form, show_in_footer: e.target.checked })}
                />
                Sitenin footer bandında göster
              </label>
              <label className="col-span-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="accent-cyan-500"
                  checked={form.embed_enabled}
                  onChange={(e) => setForm({ ...form, embed_enabled: e.target.checked })}
                />
                Banner kodu üretilsin (diğer sitelere verilebilir)
              </label>
              <button disabled={busy || uploading} onClick={save} className="admin-btn-primary col-span-2 py-3">
                {busy && <Loader2 size={14} className="animate-spin inline mr-2" />}Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {embed && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" onClick={() => setEmbed(null)}>
          <div className="absolute inset-0" style={{ background: 'var(--v2-surface, rgba(0,0,0,0.75))', backdropFilter: 'blur(6px)' }} />
          <div className="relative admin-card w-full max-w-3xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/5 flex items-center gap-2">
              <Code2 size={16} className="text-cyan-400" />
              <h3 className="font-bold">{embed.name} — Banner Kodu</h3>
              <button onClick={() => setEmbed(null)} className="icon-btn view ml-auto"><X size={14} /></button>
            </div>
            <div className="p-5 space-y-4">
              {!embed.embed_enabled && (
                <div className="rounded-lg border border-amber-400/25 bg-amber-400/5 px-3 py-2 text-xs text-amber-200">
                  Bu kayıtta banner kodu kapalı. Kod aşağıda üretilir ama adres
                  404 döner — düzenleme penceresinden “Banner kodu üretilsin”
                  seçeneğini açın.
                </div>
              )}
              <div>
                <div className="text-xs text-slate-400 mb-2">Banner ölçüsü</div>
                <div className="flex flex-wrap gap-2">
                  {meta.sizes.map((size) => (
                    <button
                      key={size.key}
                      type="button"
                      onClick={() => pickSize(size.key)}
                      className={`badge-pill ${embedSize === size.key ? 'badge-blue' : 'badge-gray'}`}
                      style={{ cursor: 'pointer' }}
                    >
                      {size.label} · {size.width}×{size.height}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="text-xs text-slate-400 mb-2">Önizleme</div>
                <div className="rounded-lg bg-slate-900/60 p-4 overflow-auto">
                  {embedLoading || !embedCode ? (
                    <div className="py-8 text-center"><Loader2 className="inline animate-spin text-cyan-400" /></div>
                  ) : (
                    <iframe
                      key={`${embed.id}-${embedSize}`}
                      title="Banner önizleme"
                      src={embedCode.preview_url}
                      width={embedCode.width}
                      height={embedCode.height}
                      style={{ border: 0, display: 'block', margin: '0 auto' }}
                      scrolling="no"
                    />
                  )}
                </div>
              </div>

              {embedCode && (
                <>
                  <CodeBox
                    label="iframe kodu (her sitede çalışır)"
                    value={embedCode.iframe}
                    onCopy={copy}
                  />
                  <CodeBox
                    label="script kodu (WordPress ve benzeri sistemler için)"
                    value={embedCode.script}
                    onCopy={copy}
                  />
                  <CodeBox
                    label="düz bağlantı (iframe’e izin vermeyen forum/blog için)"
                    value={embedCode.link}
                    onCopy={copy}
                  />
                  <p className="text-[11px] text-slate-500">
                    Kod {embedCode.site_base || 'site adresiniz'} üzerinden çalışır.
                    Tıklamalar sayılır ve kart üzerinde gösterilir.
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const CodeBox = ({ label, value, onCopy }) => (
  <div>
    <div className="flex items-center gap-2 mb-1">
      <span className="text-xs text-slate-400">{label}</span>
      <button
        type="button"
        onClick={() => onCopy(value, 'Kod')}
        className="admin-btn-secondary ml-auto flex items-center gap-1 px-2 py-1 text-[11px]"
      >
        <Copy size={11} /> Kopyala
      </button>
    </div>
    <textarea
      readOnly
      value={value}
      onFocus={(event) => event.target.select()}
      className="admin-input font-mono text-[11px]"
      style={{ minHeight: 68, padding: 10 }}
    />
  </div>
);

const F = ({ l, children, full }) => (
  <div className={full ? 'col-span-2' : ''}>
    <label className="text-xs text-slate-400 block mb-1">{l}</label>
    {children}
  </div>
);

export default Sponsorships;
